import { api } from "./api";
import { apiRexxbot } from "./apiRexxbot";

const PHONE_CACHE_KEY = "@Inoveclube:cartReminderPhone";
const PHONE_CACHE_MAX_AGE = 5 * 60 * 1000;
const MAX_ITEMS = 30;
const MAX_ITEM_NAME_LENGTH = 80;
const MAX_CUSTOMER_NAME_LENGTH = 100;

let pendingPhoneRequest = null;

function readPhoneCache(token) {
	try {
		const cache = JSON.parse(sessionStorage.getItem(PHONE_CACHE_KEY));
		const isValid =
			cache?.token === token && Date.now() - cache.fetchedAt <= PHONE_CACHE_MAX_AGE;

		return isValid ? { telefone: cache.telefone } : null;
	} catch (error) {
		return null;
	}
}

function writePhoneCache(token, telefone) {
	try {
		sessionStorage.setItem(
			PHONE_CACHE_KEY,
			JSON.stringify({ token, fetchedAt: Date.now(), telefone }),
		);
	} catch (error) {
		// sessionStorage cheio ou indisponível: segue sem cache.
	}
}

/**
 * Monta a lista de itens do lembrete de carrinho abandonado a partir do carrinho
 * (listBuying), respeitando os limites da API: nome até 80 caracteres, quantidade de
 * 1 a 99 e no máximo 30 itens.
 */
export function buildCartReminderItems(listBuying) {
	return listBuying.slice(0, MAX_ITEMS).map((product) => {
		const name = product.sizeDescription
			? `${product.name} ${product.sizeDescription}`
			: product.name;

		return {
			nome: String(name).slice(0, MAX_ITEM_NAME_LENGTH),
			quantidade: Math.min(99, Math.max(1, Number(product.addCounterProduct) || 1)),
		};
	});
}

/**
 * Busca o telefone do cliente logado na rota /v1/person/shopping.
 * Reaproveita a última resposta salva no sessionStorage (inclusive quando o cliente
 * não tem telefone, para não repetir a requisição a cada mudança do carrinho).
 * Chamadas simultâneas compartilham a mesma requisição.
 * Resolve null quando não há token, telefone ou a requisição falha.
 */
export async function getCustomerPhone() {
	const token = localStorage.getItem("@Inoveclube:token");
	if (!token) return null;

	const cached = readPhoneCache(token);
	if (cached) return cached.telefone;

	if (!pendingPhoneRequest) {
		pendingPhoneRequest = api
			.get("/v1/person/shopping", { headers: { token } })
			.then((response) => {
				const telefone = response.data?.body?.userInfo?.telefone || null;
				writePhoneCache(token, telefone);
				return telefone;
			})
			.catch(() => null)
			.finally(() => {
				pendingPhoneRequest = null;
			});
	}

	return pendingPhoneRequest;
}

/**
 * Avisa a API de lembrete de carrinho abandonado. Nunca lança erro nem mostra toast:
 * o lembrete é um extra e não pode atrapalhar a compra.
 */
async function sendCartReminder(route, { cdEmpresa, ...data }) {
	try {
		if (!cdEmpresa) return;

		const telefone = await getCustomerPhone();
		if (!telefone) return;

		await apiRexxbot.post(route, { cdEmpresa, telefone, ...data });
	} catch (error) {
		// Falha no lembrete é ignorada: segue o fluxo normal da compra.
	}
}

/**
 * Registra que o carrinho tem itens (reinicia a contagem de 10 min no servidor).
 * Envia sempre o carrinho completo em `itens`.
 */
export function notifyCartItemAdded({ cdEmpresa, itens }) {
	const nome = localStorage.getItem("@Inoveclube:userName");

	return sendCartReminder("carrinho/item-adicionado", {
		cdEmpresa,
		nome: nome ? nome.slice(0, MAX_CUSTOMER_NAME_LENGTH) : undefined,
		itens,
	});
}

/**
 * Cancela o lembrete: pedido concluído ou carrinho esvaziado.
 */
export function notifyCartFinished({ cdEmpresa }) {
	return sendCartReminder("carrinho/finalizado", { cdEmpresa });
}
